/* eslint-disable no-console */
import { CheckIcon, RepeatIcon } from '@chakra-ui/icons';
import {
  Box,
  Alert,
  AlertIcon,
  VStack,
  Divider,
  Button,
  HStack,
  IconButton,
  Tooltip,
  useToast,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import Editor, { useMonaco } from '@monaco-editor/react';
import monaco from 'monaco-editor';
import React, { useEffect, useRef, useState } from 'react';

import { colors } from '../colors';
import SplitPane from './SplitPane';

const editorOptions: monaco.editor.IEditorOptions = {
  fontSize: 16,
  minimap: { enabled: false },
  automaticLayout: true,
  wordWrap: 'on',
  scrollBeyondLastLine: false,
  renderWhitespace: 'selection',
  formatOnType: true,
  formatOnPaste: true,
  quickSuggestions: { other: 'inline', comments: false, strings: true },
  suggest: { snippetsPreventQuickSuggestions: false },
};

// JSON editor that uses monaco editor and json schema and lets you edit JSON contents with validation
export const SchemaEditor = ({
  contents,
  nodeId,
  schema,
  children,
  onSave,
  newConfig,
  direction = 'horizontal',
  height = 600,
}: {
  contents: object;
  nodeId: string;
  schema: object;
  children?: React.ReactNode;
  newConfig: boolean;
  onSave: (nodeId: string, newValue: object) => Promise<void>;
  direction?: 'horizontal' | 'vertical';
  height?: number | string;
}) => {
  const toast = useToast();
  const { colorMode } = useColorMode();
  const borderColor = useColorModeValue(colors.gray[300], colors.gray[600]);
  const monacoTheme = colorMode === 'dark' ? 'vs-dark' : 'vs';

  const monacoObj = useMonaco();
  useEffect(() => {
    if (monacoObj) {
      const schemaUri = `schema-${nodeId}`;
      const fileMatch = [`*-${nodeId}`];
      monacoObj.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemaValidation: 'warning',
        schemas: [
          {
            uri: schemaUri,
            fileMatch,
            schema,
          },
        ],
      });
    }
  }, [monacoObj, schema, nodeId]);

  const [errors, setErrors] = useState<string[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [value, setValue] = useState<string>(JSON.stringify(contents, null, 4));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [editorSize, setEditorSize] = useState(
    direction === 'horizontal' ? 70 : 60,
  );
  const originalValueRef = useRef<string>(JSON.stringify(contents, null, 4));
  const handleSaveRef = useRef<() => void>(() => {});

  // Sync incoming contents/nodeId
  useEffect(() => {
    setErrors([]);
    setDirty(false);
    const next = JSON.stringify(contents, null, 4);
    setValue(next);
    originalValueRef.current = next;
  }, [contents, nodeId]);

  // Save handler kept in a ref for stable callbacks
  useEffect(() => {
    handleSaveRef.current = async () => {
      if (errors.length > 0 || isSaving) return;
      if (!dirty && !newConfig) return;
      setIsSaving(true);
      try {
        const parsed = JSON.parse(value);
        await onSave(nodeId, parsed);
        setErrors([]);
        setDirty(false);
        toast({
          status: 'success',
          title: 'Saved',
          duration: 2500,
          isClosable: true,
        });
      } catch (e: any) {
        const message = e?.message || 'Failed to save';
        setErrors((prev) => [message, ...prev]);
        toast({
          status: 'error',
          title: 'Save failed',
          description: message,
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setIsSaving(false);
      }
    };
  }, [dirty, errors.length, isSaving, newConfig, onSave, nodeId, toast, value]);

  const maxErrorsToShow = 3;
  const topErrors = errors.slice(0, maxErrorsToShow);
  const moreErrors = Math.max(errors.length - maxErrorsToShow, 0);

  return (
    <SplitPane
      direction={direction}
      defaultSize={editorSize}
      onChange={(p) => setEditorSize(p)}
      height={height}
      start={
        <Box height="100%">
          <Editor
            key={nodeId}
            path={`${nodeId}-${nodeId}`}
            defaultLanguage="json"
            value={value}
            theme={monacoTheme}
            options={editorOptions}
            onChange={(output) => {
              if (typeof output === 'string') {
                setValue(output);
                setDirty(true);
              }
            }}
            onValidate={(newMarkers) => {
              const msgs = newMarkers.map((m) => m.message).filter(Boolean);
              setErrors(Array.from(new Set(msgs)));
            }}
          />
        </Box>
      }
      end={
        <Box
          p="16px"
          borderLeft={
            direction === 'horizontal' ? `1px solid ${borderColor}` : 'none'
          }
          borderTop={
            direction === 'vertical' ? `1px solid ${borderColor}` : 'none'
          }
          display="flex"
          flexDirection="column"
          minWidth={direction === 'horizontal' ? '200px' : 'auto'}
          minHeight={direction === 'vertical' ? '200px' : 'auto'}
          height="100%"
        >
          {children}
          {/* Toolbar */}
          <HStack spacing="8px" mb="8px">
            <Button
              colorScheme="v2-aurora"
              leftIcon={<CheckIcon />}
              isLoading={isSaving}
              isDisabled={!!errors.length || isSaving || (!dirty && !newConfig)}
              onClick={() => handleSaveRef.current()}
            >
              {newConfig ? 'Create Config' : 'Save Changes'}
            </Button>
            <Tooltip label="Reset to original">
              <IconButton
                aria-label="Reset"
                icon={<RepeatIcon />}
                onClick={() => {
                  const orig = originalValueRef.current;
                  setValue(orig);
                  setDirty(false);
                  setErrors([]);
                }}
                isDisabled={!dirty}
                variant="outline"
                size="sm"
              />
            </Tooltip>
          </HStack>
          <Divider orientation="horizontal" my="16px" />
          <VStack align="left" spacing="8px" flex="1">
            {topErrors.map((error) => (
              <Alert
                key={error}
                status="error"
                variant="subtle"
                wordBreak="break-all"
              >
                <AlertIcon />
                {error}
              </Alert>
            ))}
            {moreErrors ? (
              <Alert status="error" variant="subtle" wordBreak="break-all">
                <AlertIcon />
                and {moreErrors} more errors
              </Alert>
            ) : null}
            {isSaving ? (
              <Alert status="info" variant="subtle" wordBreak="break-all">
                <AlertIcon />
                Saving...
              </Alert>
            ) : null}
          </VStack>
        </Box>
      }
    />
  );
};
